import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import useComments from "../hooks/useComments";
import CommentEditor from "./CommentEditor";
import CommentList from "./CommentList";
import useUsers from "@/features/user/hooks/useUsers";
const getStorageKey = (taskId, userId) => `task-${taskId}-comment-draft-${userId ?? "anonymous"}`;
const parseHtmlToPlainText = (html) => {
    if (!html)
        return "";
    const parser = new DOMParser();
    const dom = parser.parseFromString(html, "text/html");
    return dom.body.textContent ?? "";
};
const CommentsSection = ({ taskId, currentUserId }) => {
    const { comments, loading, error, handleAddComment, handleUpdateComment, handleDeleteComment, } = useComments(taskId);
    const { users } = useUsers(undefined, undefined, true);
    const mentionOptions = useMemo(() => users.map((user) => ({
        id: user.id.toString(),
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email,
    })), [users]);
    const storageKey = useMemo(() => getStorageKey(taskId, currentUserId), [taskId, currentUserId]);
    const [editorKey, setEditorKey] = useState(0);
    const [isEditorVisible, setIsEditorVisible] = useState(false);
    const [draftSnapshot, setDraftSnapshot] = useState({
        html: "",
        plainText: "",
        characterCount: 0,
    });
    const [optimisticComments, setOptimisticComments] = useState([]);
    const [submitError, setSubmitError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResettingDraft, setIsResettingDraft] = useState(false);
    const [editingComment, setEditingComment] = useState(null);
    const [editingSnapshot, setEditingSnapshot] = useState({
        html: "",
        plainText: "",
        characterCount: 0,
    });
    const [editingError, setEditingError] = useState(null);
    const [isEditingSubmitting, setIsEditingSubmitting] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const stored = window.localStorage.getItem(storageKey);
        if (!stored)
            return;
        try {
            const metadata = JSON.parse(stored);
            const plain = parseHtmlToPlainText(metadata.content);
            setDraftSnapshot({
                html: metadata.content,
                plainText: plain,
                characterCount: plain.trim().length,
            });
            setIsEditorVisible(true);
        }
        catch (err) {
            console.error("Impossible de charger le brouillon", err);
        }
    }, [storageKey]);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const timeout = window.setTimeout(() => {
            if (draftSnapshot.html ||
                draftSnapshot.plainText.trim().length > 0) {
                const metadata = {
                    content: draftSnapshot.html,
                    updatedAt: Date.now(),
                };
                window.localStorage.setItem(storageKey, JSON.stringify(metadata));
            }
            else {
                window.localStorage.removeItem(storageKey);
            }
        }, 600);
        return () => window.clearTimeout(timeout);
    }, [draftSnapshot, storageKey]);
    useEffect(() => {
        setOptimisticComments((previous) => previous.filter((optimistic) => !comments.some((comment) => comment.id === optimistic.id)));
    }, [comments]);
    const mergedComments = useMemo(() => {
        const merged = new Map();
        [...comments, ...optimisticComments].forEach((comment) => {
            merged.set(comment.id, comment);
        });
        return Array.from(merged.values()).sort((a, b) => {
            const fallback = Number.MAX_SAFE_INTEGER;
            const dateA = a.created_at
                ? new Date(a.created_at).getTime()
                : fallback;
            const dateB = b.created_at
                ? new Date(b.created_at).getTime()
                : fallback;
            return dateA - dateB;
        });
    }, [comments, optimisticComments]);
    const handleDraftChange = useCallback((snapshot) => {
        setSubmitError(null);
        if (isResettingDraft) {
            if (snapshot.plainText.trim().length === 0) {
                setIsResettingDraft(false);
                setDraftSnapshot(snapshot);
            }
            return;
        }
        setDraftSnapshot(snapshot);
    }, [isResettingDraft]);
    const handlePublish = useCallback(async (payload) => {
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            const comment = await handleAddComment({
                content: payload.html,
            });
            setOptimisticComments((previous) => [comment, ...previous]);
            setIsResettingDraft(true);
            setDraftSnapshot({ html: "", plainText: "", characterCount: 0 });
            setEditorKey((previous) => previous + 1);
            if (typeof window !== "undefined") {
                window.localStorage.removeItem(storageKey);
            }
            setIsEditorVisible(false);
        }
        catch (err) {
            setSubmitError(err.message ?? "Impossible de publier le commentaire");
        }
        finally {
            setIsSubmitting(false);
        }
    }, [handleAddComment, storageKey]);
    const handleDelete = useCallback(async (comment) => {
        const confirmation = window.confirm("Supprimer définitivement ce commentaire ?");
        if (!confirmation)
            return;
        await handleDeleteComment(comment.id);
    }, [handleDeleteComment]);
    const startEditing = useCallback((comment) => {
        setEditingError(null);
        setEditingComment(comment);
        const plain = parseHtmlToPlainText(comment.content);
        setEditingSnapshot({
            html: comment.content,
            plainText: plain,
            characterCount: plain.trim().length,
        });
    }, []);
    const handleEditChange = useCallback((snapshot) => {
        setEditingSnapshot(snapshot);
    }, []);
    const handleEditSubmit = useCallback(async (payload) => {
        if (!editingComment)
            return;
        setEditingError(null);
        setIsEditingSubmitting(true);
        try {
            const existingIds = editingComment.attachments
                ?.map((attachment) => attachment.id)
                .filter((id) => typeof id === "number") ?? [];
            await handleUpdateComment(editingComment.id, {
                content: payload.html,
                attachmentIds: existingIds.length ? existingIds : undefined,
            });
            setEditingComment(null);
        }
        catch (err) {
            setEditingError(err.message ?? "Impossible de modifier le commentaire");
        }
        finally {
            setIsEditingSubmitting(false);
        }
    }, [editingComment, handleUpdateComment]);
    const mentionProps = useMemo(() => mentionOptions.slice(0, 30), [mentionOptions]);
    return (_jsxs("section", { className: "mt-8 space-y-6", "aria-label": "Commentaires de la t\u00E2che", children: [_jsx("header", { className: "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", children: _jsx("div", { className: "space-y-1", children: _jsx("h2", { className: "text-l font-semibold text-gray-800", children: "Commentaires" }) }) }), loading && (_jsx("p", { className: "text-sm text-slate-500", children: "Chargement des commentaires\u2026" })), error && (_jsx("p", { className: "text-sm text-red-600", role: "alert", children: error })), !loading && mergedComments.length > 0 && (_jsx(CommentList, { comments: mergedComments, currentUserId: currentUserId, onEdit: startEditing, onDelete: handleDelete })), !loading && mergedComments.length === 0 && (_jsx("p", { className: "rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500", children: "Aucun commentaire." })), editingComment && (_jsxs("div", { className: "rounded-lg border border-blue-200 bg-blue-50 p-4", children: [_jsxs("h3", { className: "mb-3 text-sm font-semibold text-blue-700", children: ["Modification du commentaire de ", editingComment.first_name, " ", " ", editingComment.last_name] }), _jsx(CommentEditor, { value: editingSnapshot.html, onChange: handleEditChange, onSubmit: handleEditSubmit, onCancel: () => {
                            setEditingComment(null);
                            setEditingError(null);
                        }, mentionOptions: mentionProps, isSubmitting: isEditingSubmitting, submitLabel: "Mettre \u00E0 jour", cancelLabel: "Annuler", placeholder: "Mettre \u00E0 jour le commentaire..." }), editingError && (_jsx("p", { className: "mt-2 text-sm text-red-600", role: "alert", children: editingError })), editingComment.attachments && editingComment.attachments.length > 0 && (_jsx("div", { className: "mt-4 rounded border border-blue-200 bg-white p-3 text-sm text-blue-800", children: "Les pi\u00E8ces jointes existantes seront conserv\u00E9es." }))] })), !isEditorVisible && (_jsx("button", { type: "button", className: "inline-flex items-center self-start rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600", onClick: () => setIsEditorVisible(true), children: "Commentaire" })), isEditorVisible && (_jsxs(_Fragment, { children: [_jsx(CommentEditor, { value: draftSnapshot.html, onChange: handleDraftChange, onSubmit: handlePublish, onCancel: () => {
                            setIsResettingDraft(true);
                            setDraftSnapshot({
                                html: "",
                                plainText: "",
                                characterCount: 0,
                            });
                            setEditorKey((previous) => previous + 1);
                            setSubmitError(null);
                            setIsEditorVisible(false);
                            if (typeof window !== "undefined") {
                                window.localStorage.removeItem(storageKey);
                            }
                        }, mentionOptions: mentionProps, isSubmitting: isSubmitting }, editorKey), submitError && (_jsx("p", { className: "text-sm text-red-600", role: "alert", children: submitError }))] }))] }));
};
export default CommentsSection;
